"use client";

import { createContext, useContext, useState, useEffect } from "react";
import apiEndpoints from "../apiConfig"; // Make sure path is correct
import { useToast } from "./ToastContext";

const AuthContext = createContext(undefined);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { showToast } = useToast(); // ✅ get toast

  // Load user from localStorage on mount
  useEffect(() => {
    const storedUser = localStorage.getItem("matrimony_user");
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      parsedUser.isVerified = Number(parsedUser.isVerified); // force number
      setUser(parsedUser);
    }
    setIsLoading(false);
  }, []);

  // Login function
  const login = async (email, password) => {
    setIsLoading(true);
    try {
      const res = await fetch(apiEndpoints.login, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "login", identifier: email, password }),
      });

      const text = await res.text();
      if (!res.ok) {
        throw new Error(
          `Server Error (${res.status}): ${text.substring(0, 100) || "Empty response"}`,
        );
      }

      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        console.error("Failed to parse login JSON:", text);
        throw new Error("Invalid server response");
      }

      if (data.success) {
        data.user.isVerified = Number(data.user.isVerified);
        setUser(data.user);
        localStorage.setItem("matrimony_user", JSON.stringify(data.user));
        localStorage.setItem("id", data.user.id);
        localStorage.setItem("user_id", data.user.user_id); // custom ID
        setIsLoading(false);
        return data.user;
      } else {
        setIsLoading(false);
        throw new Error(data.message || "Login failed");
      }
    } catch (err) {
      setIsLoading(false);
      throw err;
    }
  };

  // Signup function
  const signup = async (email, password, name) => {
    setIsLoading(true);
    await new Promise((resolve) => setTimeout(resolve, 1000));

    const newUser = {
      id: Date.now().toString(),
      email,
      name,
      profileComplete: false,
      isVerified: 0, // Unverified at signup
    };

    setUser(newUser);
    localStorage.setItem("matrimony_user", JSON.stringify(newUser));
    setIsLoading(false);
    return newUser;
  };

  // Logout function
  const logout = () => {
    setUser(null);
    localStorage.removeItem("matrimony_user");
    localStorage.removeItem("id");
    localStorage.removeItem("user_id");
    showToast("Logged out successfully", "success"); // ✅ same style toast as favourites (light green)
  };

  return (
    <AuthContext.Provider
      value={{ user, setUser, login, signup, logout, isLoading }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// Custom hook
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
